import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import type {
  AdminPackage,
  FetchPackagesParams,
  PackagePayload,
  PackagesState,
} from "cmspages/Packages/types";

type ApiResponse<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return (
      axiosError.response.data?.message ||
      "Something went wrong. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

const extractList = (responseData: unknown): AdminPackage[] => {
  if (Array.isArray(responseData)) return responseData as AdminPackage[];

  if (!responseData || typeof responseData !== "object") return [];

  const data = responseData as {
    data?: unknown;
    packages?: unknown;
    results?: unknown;
  };

  if (Array.isArray(data.data)) return data.data as AdminPackage[];
  if (Array.isArray(data.packages)) return data.packages as AdminPackage[];
  if (Array.isArray(data.results)) return data.results as AdminPackage[];

  return [];
};

const extractItem = (responseData: unknown): AdminPackage | null => {
  if (!responseData || typeof responseData !== "object") return null;

  const data = responseData as { data?: unknown };
  const candidate = data.data || responseData;

  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return candidate as AdminPackage;
  }

  return null;
};

const buildPackageQuery = (params?: FetchPackagesParams) => {
  const searchParams = new URLSearchParams();

  if (params?.search?.trim()) {
    searchParams.append("search", params.search.trim());
  }

  if (params?.billing_cycle) {
    searchParams.append("billing_cycle", params.billing_cycle);
  }

  if (params?.status) {
    searchParams.append("status", params.status);
  }

  const query = searchParams.toString();
  return query ? `${API_ENDPOINTS.packages.list}?${query}` : API_ENDPOINTS.packages.list;
};

const buildPackagePayload = (payload: PackagePayload): PackagePayload => ({
  ...payload,
  currency: "NGN",
});

export const fetchPackages = createAsyncThunk<
  AdminPackage[],
  FetchPackagesParams | undefined,
  { rejectValue: string }
>("packages/fetchPackages", async (params, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiResponse<AdminPackage[]>>(
      buildPackageQuery(params)
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to fetch packages."
      );
    }

    return extractList(response.data);
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const createPackage = createAsyncThunk<
  AdminPackage,
  PackagePayload,
  { rejectValue: string }
>("packages/createPackage", async (payload, { rejectWithValue }) => {
  try {
    const requestPayload = buildPackagePayload(payload);
    const response = await axiosInstance.post<ApiResponse<AdminPackage>>(
      API_ENDPOINTS.packages.create,
      requestPayload
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to create package."
      );
    }

    const packageItem = extractItem(response.data);

    if (!packageItem) {
      return rejectWithValue("Unable to create package.");
    }

    return packageItem;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const updatePackage = createAsyncThunk<
  AdminPackage,
  { id: number; payload: PackagePayload },
  { rejectValue: string }
>("packages/updatePackage", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const requestPayload = buildPackagePayload(payload);
    const response = await axiosInstance.put<ApiResponse<AdminPackage>>(
      API_ENDPOINTS.packages.update(id),
      requestPayload
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to update package."
      );
    }

    const packageItem = extractItem(response.data);

    if (!packageItem) {
      return rejectWithValue("Unable to update package.");
    }

    return packageItem;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const deletePackage = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("packages/deletePackage", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<ApiResponse<unknown>>(
      API_ENDPOINTS.packages.delete(id)
    );

    if (response.data?.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to delete package."
      );
    }

    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const initialState: PackagesState = {
  packages: [],
  loading: false,
  actionLoading: false,
  error: null,
};

const packagesSlice = createSlice({
  name: "packages",
  initialState,
  reducers: {
    clearPackagesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPackages.fulfilled,
        (state, action: PayloadAction<AdminPackage[]>) => {
          state.packages = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unable to fetch packages.";
      })
      .addCase(createPackage.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createPackage.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.packages = [action.payload, ...state.packages];
      })
      .addCase(createPackage.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to create package.";
      })
      .addCase(updatePackage.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updatePackage.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.packages = state.packages.map((packageItem) =>
          packageItem.id === action.payload.id ? action.payload : packageItem
        );
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to update package.";
      })
      .addCase(deletePackage.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.packages = state.packages.filter(
          (packageItem) => packageItem.id !== action.payload
        );
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to delete package.";
      });
  },
});

export const { clearPackagesError } = packagesSlice.actions;
export default packagesSlice.reducer;
